import * as React from 'react'
import { Logger } from 'src/3rds/cube'

import { makeStyles } from 'tss-react/mui'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined'

import {
    agruparCnae,
    comValor,
    type LinhaDaArvore,
    linhasDaArvore,
    moeda,
    moedaInteira,
    numero,
    opcaoSerie
} from '../Consultas.graficos'
import { type Consulta, CONSULTAS_POR_PAGINA, DashboardsScope, type PontoMapa } from '../Dashboards.scopes'
import { percentual } from '../Negocios.graficos'
import { BarraDeFiltros } from './ConsultasFiltros'
import { Numero, useCardStyles } from './Componentes'
import { EChart } from './EChart'
import { MapaConsultas } from './MapaConsultas'

const LOG = Logger.get('Consultas-VIEW')

const dataHora = (iso?: string) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }) : ''
const inteiro = (n: number | null | undefined) => (n == null ? '' : numero(n))
const cnpjFormatado = (c: string) =>
    c.length === 14 ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}` : c

const useStyles = makeStyles()((theme) => ({
    blocos: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: theme.spacing(2.5)
    },
    numeros: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: theme.spacing(2)
    },
    lado: {
        display: 'flex',
        gap: theme.spacing(2.5),
        flexWrap: 'wrap',
        '& > *': {
            flex: '1 1 460px',
            minWidth: 0
        }
    },
    rolagem: {
        maxHeight: 440,
        overflow: 'auto',
        '& th, & td': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1)
        },
        '& th': {
            lineHeight: 1.25
        }
    },
    // A tabela de detalhe tem as colunas do Power BI: rola na horizontal e o cabeçalho fica fixo.
    detalhe: {
        maxHeight: 560,
        overflow: 'auto',
        '& th, & td': {
            whiteSpace: 'nowrap',
            fontSize: 13
        }
    },
    total: {
        '& td': {
            fontWeight: 700,
            position: 'sticky',
            bottom: 0,
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`
        }
    },
    nome: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: 0
    },
    alternar: {
        width: 28,
        flexShrink: 0,
        display: 'inline-flex',
        justifyContent: 'center'
    },
    linkFiltro: {
        maxWidth: 260,
        cursor: 'pointer',
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    mensagem: {
        maxWidth: 360,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }
}))

/** Colunas do detalhe, na ordem da tabela "CNPJ consultado x Parceiro" do Power BI. */
const COLUNAS: { titulo: string; valor: (c: Consulta) => React.ReactNode; direita?: boolean }[] = [
    { titulo: 'Data e hora', valor: (c) => dataHora(c.quando) },
    { titulo: 'Owner_id', valor: (c) => c.ownerId },
    { titulo: 'Parceiro que consultou', valor: (c) => c.parceiro },
    { titulo: 'CNPJ', valor: (c) => c.cnpj },
    { titulo: 'Razão social', valor: (c) => c.razaoSocial },
    { titulo: 'Matriz ou filial', valor: (c) => c.matrizFilial },
    { titulo: 'Coligadas', valor: (c) => inteiro(c.coligadas), direita: true },
    { titulo: 'Funcionários', valor: (c) => inteiro(c.funcionarios), direita: true },
    { titulo: 'Funcionários coligadas', valor: (c) => inteiro(c.funcionariosColigadas), direita: true },
    { titulo: 'Renovações do negócio', valor: (c) => inteiro(c.renovacoesNegocio), direita: true },
    { titulo: 'Classificação', valor: (c) => c.classificacao },
    { titulo: 'deal_id', valor: (c) => c.dealId ?? '' },
    { titulo: 'Negócio criado em', valor: (c) => dataHora(c.negocioCriadoEm) },
    { titulo: 'Parceiro do negócio', valor: (c) => c.parceiroNegocio ?? '' },
    { titulo: 'Porte', valor: (c) => c.porte },
    { titulo: 'Cód. CNAE', valor: (c) => c.cnae },
    { titulo: 'Descrição CNAE', valor: (c) => c.descricaoCnae },
    { titulo: 'Grupo CNAE', valor: (c) => c.grupoCnae },
    { titulo: 'Seção CNAE', valor: (c) => c.secaoCnae },
    {
        titulo: 'Faturamento estimado',
        valor: (c) => (c.faturamento == null ? '' : moeda(c.faturamento)),
        direita: true
    },
    { titulo: 'UF', valor: (c) => c.uf },
    { titulo: 'Município', valor: (c) => c.municipio },
    { titulo: 'Idade da empresa', valor: (c) => inteiro(c.idadeEmpresa), direita: true },
    { titulo: 'Natureza jurídica', valor: (c) => c.naturezaJuridica },
    { titulo: 'Nome do negócio', valor: (c) => c.nomeNegocio ?? '' },
    { titulo: 'Nome fantasia', valor: (c) => c.nomeFantasia },
    { titulo: 'Optante Simples', valor: (c) => c.optanteSimples },
    { titulo: 'Situação cadastral', valor: (c) => c.situacaoCadastral },
    { titulo: 'Mensagem', valor: (c) => <Mensagem texto={c.mensagem} /> }
]

function Mensagem({ texto }: { texto: string }) {
    const { classes } = useStyles()
    if (!texto) return null
    return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{texto}</span>} placement="left">
            <div className={classes.mensagem}>{texto.replace(/\s+/g, ' ')}</div>
        </Tooltip>
    )
}

/** Dashboard de consultas de lead (E19): o "Automação Comercial - AG" do Power BI, dentro do portal. */
export function ConsultasPainel({ scope }: { scope: DashboardsScope }) {
    const { classes } = useStyles()
    const { classes: cards } = useCardStyles()
    const consultas = scope.consultas!

    const serie = React.useMemo(() => opcaoSerie(consultas), [consultas])
    const divisoes = React.useMemo(() => agruparCnae(consultas.cnae), [consultas])
    const arvore = React.useMemo(
        () => linhasDaArvore(consultas.parceiros, scope.parceirosAbertos, scope.filhosParceiros),
        [consultas, scope.parceirosAbertos, scope.filhosParceiros]
    )
    const [divisoesAbertas, setDivisoesAbertas] = React.useState<string[]>([])

    const filtrar = (filtros: typeof scope.filtrosConsultas, nomes?: Record<string, string>) =>
        scope.onFiltrosConsultasChange({ filtros, nomes }).catch(LOG.caught)
    const filtrarRede = (ownerId: string, nome: string) =>
        filtrar({ ...scope.filtrosConsultas, rede: ownerId }, { [ownerId]: nome })
    const filtrarMunicipio = (p: PontoMapa) =>
        filtrar(comValor(scope.filtrosConsultas, 'municipios', `${p.uf}|${p.municipio}`))
    const alternar = (caminho: string[]) => scope.onAlternarParceiro(caminho).catch(LOG.caught)
    const emitPagina = (_evt: unknown, pagina: number) => scope.onDetalhePaginaChange(pagina).catch(LOG.caught)

    return (
        <Box className={classes.blocos}>
            <BarraDeFiltros scope={scope} />

            <Box className={classes.numeros}>
                <Numero
                    titulo="Total de CNPJs solicitados"
                    valor={numero(consultas.consultas)}
                    nota={`${numero(consultas.cnpjs)} CNPJs distintos · ${numero(
                        consultas.cadastros
                    )} cadastrados no CRM (${percentual(consultas.cadastros, consultas.consultas)})`}
                />
                <Numero
                    titulo="Total investido"
                    valor={moeda(consultas.investido)}
                    nota={`${moeda(consultas.valorPorConsulta)} por consulta, como no Power BI`}
                />
                <Card className={cards.card}>
                    <CardContent sx={{ pb: '12px !important' }}>
                        <Typography color="text.secondary" fontSize={14}>
                            Total de CNPJs no período
                        </Typography>
                        <EChart
                            option={serie}
                            height={78}
                            ariaLabel={`Consultas por ${consultas.granularidade === 'mes' ? 'mês' : 'dia'} no período`}
                        />
                    </CardContent>
                </Card>
            </Box>

            <Box className={classes.lado}>
                <Card className={cards.card}>
                    <CardContent>
                        <Typography className={cards.tituloCard}>Solicitações na LIA por parceiro</Typography>
                        <Typography className={cards.nota}>
                            Cada parceiro soma a rede abaixo dele: abra com o + para ver os níveis. Clique no nome para
                            filtrar o painel pela rede.
                        </Typography>
                        <TableContainer className={classes.rolagem} sx={{ mt: 1 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Nome</TableCell>
                                        <TableCell align="right">Empresas solicitadas</TableCell>
                                        <TableCell align="right">Renovações</TableCell>
                                        <TableCell align="right">Cadastrados CRM</TableCell>
                                        <TableCell align="right">Investimento</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {arvore.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>Nenhuma consulta com estes filtros.</TableCell>
                                        </TableRow>
                                    )}
                                    {arvore.map((linha) => (
                                        <LinhaParceiro
                                            key={linha.chave}
                                            linha={linha}
                                            carregando={scope.carregandoParceiro === linha.chave}
                                            onAlternar={alternar}
                                            onFiltrar={filtrarRede}
                                        />
                                    ))}
                                    <TableRow className={classes.total}>
                                        <TableCell>Total</TableCell>
                                        <TableCell align="right">{numero(consultas.consultas)}</TableCell>
                                        <TableCell align="right">{numero(consultas.renovacoes)}</TableCell>
                                        <TableCell align="right">{numero(consultas.cadastros)}</TableCell>
                                        <TableCell align="right">{moedaInteira(consultas.investido)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Card className={cards.card}>
                    <CardContent>
                        <Typography className={cards.tituloCard}>Onde estão as empresas consultadas</Typography>
                        <Typography className={cards.nota}>
                            Um círculo por município, do tamanho dos CNPJs consultados. Clique para filtrar.
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <MapaConsultas pontos={consultas.mapa} onMunicipio={filtrarMunicipio} />
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Card className={cards.card}>
                {scope.carregandoDetalhe && <LinearProgress />}
                <CardContent>
                    <Typography className={cards.tituloCard}>CNPJ consultado x Parceiro</Typography>
                    <Typography className={cards.nota}>
                        Cada consulta, da mais recente para a mais antiga, com os dados atuais da empresa e do negócio.
                        Passe o mouse na mensagem para ler a resposta inteira da LIA.
                    </Typography>
                    <TableContainer className={classes.detalhe} sx={{ mt: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {COLUNAS.map((c) => (
                                        <TableCell key={c.titulo} align={c.direita ? 'right' : 'left'}>
                                            {c.titulo}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.detalhe.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={COLUNAS.length}>
                                            Nenhuma consulta com estes filtros.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {scope.detalhe.map((consulta) => (
                                    <TableRow key={consulta.id} hover>
                                        {COLUNAS.map((c) => (
                                            <TableCell key={c.titulo} align={c.direita ? 'right' : 'left'}>
                                                {c.valor(consulta)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.detalheTotal}
                        page={scope.detalhePagina}
                        onPageChange={emitPagina}
                        rowsPerPage={CONSULTAS_POR_PAGINA}
                        rowsPerPageOptions={[CONSULTAS_POR_PAGINA]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${numero(count)}`}
                    />
                </CardContent>
            </Card>

            <Box className={classes.lado}>
                <Card className={cards.card}>
                    <CardContent>
                        <Typography className={cards.tituloCard}>Empresas mais consultadas</Typography>
                        <Typography className={cards.nota}>
                            As {consultas.maisConsultadas.length} mais pedidas no período. Clique no CNPJ para filtrar.
                        </Typography>
                        <TableContainer className={classes.rolagem} sx={{ mt: 1 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>CNPJ</TableCell>
                                        <TableCell>Empresa</TableCell>
                                        <TableCell align="right">Solicitações</TableCell>
                                        <TableCell align="right">Cadastros</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {consultas.maisConsultadas.map((e) => (
                                        <TableRow key={e.cnpj}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Link
                                                    component="button"
                                                    underline="hover"
                                                    className={classes.linkFiltro}
                                                    onClick={() =>
                                                        filtrar(comValor(scope.filtrosConsultas, 'cnpjs', e.cnpj))
                                                    }
                                                >
                                                    {cnpjFormatado(e.cnpj)}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{e.nome || '—'}</TableCell>
                                            <TableCell align="right">{numero(e.consultas)}</TableCell>
                                            <TableCell align="right">{numero(e.cadastros)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Card className={cards.card}>
                    <CardContent>
                        <Typography className={cards.tituloCard}>Consultas por atividade (CNAE)</Typography>
                        <Typography className={cards.nota}>
                            Por divisão do CNAE; abra com o + para ver as atividades. Clique na divisão para filtrar.
                        </Typography>
                        <TableContainer className={classes.rolagem} sx={{ mt: 1 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Divisão e atividade</TableCell>
                                        <TableCell align="right">Solicitações</TableCell>
                                        <TableCell align="right">Cadastros</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {divisoes.map((d) => {
                                        const aberta = divisoesAbertas.includes(d.divisao)
                                        return (
                                            <React.Fragment key={d.divisao}>
                                                <TableRow>
                                                    <TableCell>
                                                        <Box className={classes.nome}>
                                                            <span className={classes.alternar}>
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label={aberta ? 'Fechar' : 'Abrir'}
                                                                    onClick={() =>
                                                                        setDivisoesAbertas(
                                                                            aberta
                                                                                ? divisoesAbertas.filter(
                                                                                      (x) => x !== d.divisao
                                                                                  )
                                                                                : [...divisoesAbertas, d.divisao]
                                                                        )
                                                                    }
                                                                >
                                                                    {aberta ? (
                                                                        <IndeterminateCheckBoxOutlinedIcon fontSize="small" />
                                                                    ) : (
                                                                        <AddBoxOutlinedIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </span>
                                                            <Link
                                                                component="button"
                                                                underline="hover"
                                                                color="inherit"
                                                                fontWeight={600}
                                                                className={classes.linkFiltro}
                                                                onClick={() =>
                                                                    filtrar(
                                                                        comValor(
                                                                            scope.filtrosConsultas,
                                                                            'divisoes',
                                                                            d.divisao
                                                                        )
                                                                    )
                                                                }
                                                            >
                                                                {d.divisao}
                                                            </Link>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{numero(d.consultas)}</TableCell>
                                                    <TableCell align="right">{numero(d.cadastros)}</TableCell>
                                                </TableRow>
                                                {aberta &&
                                                    d.descricoes.map((a) => (
                                                        <TableRow key={`${d.divisao}|${a.descricao}`}>
                                                            <TableCell sx={{ pl: 6 }}>{a.descricao}</TableCell>
                                                            <TableCell align="right">{numero(a.consultas)}</TableCell>
                                                            <TableCell align="right">{numero(a.cadastros)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                            </React.Fragment>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>

            <Typography className={cards.nota}>
                UF, município e CNAE vêm de uma cópia do cadastro das empresas consultadas, refeita toda madrugada;
                empresas consultadas nos últimos 10 minutos podem ainda não aparecer nesses agrupamentos.
            </Typography>
        </Box>
    )
}

function LinhaParceiro({
    linha,
    carregando,
    onAlternar,
    onFiltrar
}: {
    linha: LinhaDaArvore
    carregando: boolean
    onAlternar: (caminho: string[]) => void
    onFiltrar: (ownerId: string, nome: string) => void
}) {
    const { classes } = useStyles()
    const recuo = { pl: 1 + linha.nivel * 2.5 }

    if (linha.tipo === 'proprias') {
        const m = linha.metricas
        return (
            <TableRow>
                <TableCell sx={recuo}>
                    <Box className={classes.nome}>
                        <span className={classes.alternar} />
                        <Typography fontSize={14} fontStyle="italic" color="text.secondary">
                            Consultas do próprio {linha.nome}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell align="right">{numero(m.consultas)}</TableCell>
                <TableCell align="right">{numero(m.renovacoes)}</TableCell>
                <TableCell align="right">{numero(m.cadastros)}</TableCell>
                <TableCell align="right">{moedaInteira(m.investido)}</TableCell>
            </TableRow>
        )
    }

    const { no, aberto, caminho } = linha
    return (
        <TableRow hover>
            <TableCell sx={recuo}>
                <Box className={classes.nome}>
                    <span className={classes.alternar}>
                        {carregando ? (
                            <CircularProgress size={16} />
                        ) : (
                            no.temFilhos && (
                                <IconButton
                                    size="small"
                                    aria-label={aberto ? `Fechar a rede de ${no.nome}` : `Abrir a rede de ${no.nome}`}
                                    onClick={() => onAlternar(caminho)}
                                >
                                    {aberto ? (
                                        <IndeterminateCheckBoxOutlinedIcon fontSize="small" />
                                    ) : (
                                        <AddBoxOutlinedIcon fontSize="small" />
                                    )}
                                </IconButton>
                            )
                        )}
                    </span>
                    <Tooltip title="Filtrar o painel pela rede deste parceiro" describeChild>
                        <Link
                            component="button"
                            underline="hover"
                            color="inherit"
                            fontWeight={linha.nivel === 0 ? 700 : 500}
                            className={classes.linkFiltro}
                            onClick={() => onFiltrar(no.ownerId, no.nome)}
                        >
                            {no.nome}
                        </Link>
                    </Tooltip>
                </Box>
            </TableCell>
            <TableCell align="right">{numero(no.consultas)}</TableCell>
            <TableCell align="right">{numero(no.renovacoes)}</TableCell>
            <TableCell align="right">{numero(no.cadastros)}</TableCell>
            <TableCell align="right">{moedaInteira(no.investido)}</TableCell>
        </TableRow>
    )
}

export default ConsultasPainel
